import { useState, useEffect, useRef, useCallback } from 'react';
import { Auth } from 'aws-amplify';
import appConfig from '../../../../config/app-config';

const { address: API_URL, key: API_KEY } = appConfig.apis.contractsManagementApiEnv;

export interface BillingItem {
    charge_id: string;
    installment_number: number;
    amount: number;
    due_date: string;
    emission_date: string;
    status: string;
    contract_number: string;
    contract_modality: string;
    business_reference: string;
    negotiation_type: string;
    nfe: string;
    with_invoice: boolean;
    error_message: string | null;
    payer: {
        name: string;
        document_number: string;
        address_street: string;
        address_number: string;
        address_city: string;
        address_state: string;
        address_zip_code: string;
    };
}

export interface BillingData {
    credit_operation_id: string;
    billed: boolean;
    billing_info: string;
    total_items: number;
    items: BillingItem[];
}

const POLL_INTERVAL_MS = 10000;

export const useBillingStatus = (creditOperationId?: string, billingRequested?: boolean) => {
    const [billingData, setBillingData] = useState<BillingData | null>(null);
    const [isPolling, setIsPolling] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const fetchBillingStatus = useCallback(async () => {
        if (!creditOperationId || !API_URL) return;
        try {
            const token = await Auth.currentSession().then(s => s.getIdToken().getJwtToken());
            const res = await fetch(`${API_URL}/contracts/${creditOperationId}/billing`, {
                headers: { 'Authorization': `Bearer ${token}`, 'x-api-key': API_KEY || '' }
            });
            if (!res.ok) return;
            const json = await res.json();
            const data = json.data || json;
            setBillingData(data);
            if (data.billed) stopPolling();
        } catch { /* silent */ }
    }, [creditOperationId]);

    const stopPolling = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        setIsPolling(false);
    };

    const startPolling = useCallback(() => {
        if (intervalRef.current) return;
        setIsPolling(true);
        fetchBillingStatus();
        intervalRef.current = setInterval(fetchBillingStatus, POLL_INTERVAL_MS);
    }, [fetchBillingStatus]);

    // Start polling when billing is requested and not yet billed
    useEffect(() => {
        if (billingRequested && !billingData?.billed) {
            startPolling();
        }
        return () => stopPolling();
    }, [billingRequested, startPolling]);

    return { billingData, isPolling, fetchBillingStatus, startPolling };
};
